import { useEffect, useRef, useState } from "react";
import { Upload, Check, Loader2, Image as ImageIcon } from "lucide-react";
import client from "../../lib/api";
import { PageHeader } from "./adminUi";

export default function AdminSettingsScreen() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [about, setAbout] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [logoErr, setLogoErr] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    client.get("/admin/settings").then((r) => {
      setSettings(r.data);
      setName(r.data.display_name || "");
      setAbout(r.data.about || "");
      setLogoUrl(r.data.logo_url || null);
    }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true); setSavedMsg("");
    try {
      const { data } = await client.put("/admin/settings", { display_name: name, about });
      setName(data.display_name || "");
      setAbout(data.about || "");
      setSavedMsg("Saved");
      setTimeout(() => setSavedMsg(""), 3000);
    } finally { setSaving(false); }
  };

  const onLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLogoErr(""); setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await client.post("/admin/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoUrl(data.logo_url);
    } catch (err) {
      setLogoErr(err?.response?.data?.detail || "That upload did not work.");
    } finally { setUploading(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin" size={20} /></div>;

  return (
    <div data-testid="admin-settings">
      <PageHeader title="Settings" subtitle="Your college profile and brand. Your logo appears on the student portal too." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="k-card lg:col-span-1" style={{ padding: "1.5rem" }} data-testid="settings-logo-card">
          <div className="text-sm font-medium mb-1" style={{ color: "#131822" }}>College logo</div>
          <div className="k-quiet !text-xs mb-4">PNG, JPG, WEBP, or SVG. Under 2 MB.</div>
          <div
            className="rounded-xl p-6 text-center cursor-pointer flex flex-col items-center gap-3"
            style={{ border: "1.5px dashed rgba(19,24,34,0.2)", background: "rgba(19,24,34,0.02)" }}
            onClick={() => fileRef.current?.click()}
            data-testid="settings-logo-dropzone"
          >
            {logoUrl ? (
              <img src={logoUrl} alt="college logo" className="h-16 object-contain" data-testid="settings-logo-preview" />
            ) : (
              <div className="w-16 h-16 rounded-xl flex items-center justify-center" style={{ background: "rgba(29,95,212,0.1)", color: "#1D5FD4" }}>
                <ImageIcon size={26} />
              </div>
            )}
            <button className="k-btn k-btn-ghost !py-1.5 !text-xs" disabled={uploading} data-testid="settings-logo-upload-btn">
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {logoUrl ? "Replace logo" : "Upload logo"}
            </button>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={onLogo} data-testid="settings-logo-input" />
          </div>
          {logoErr && <div className="text-xs mt-2" style={{ color: "#B3261E" }} data-testid="settings-logo-error">{logoErr}</div>}
        </div>

        <div className="k-card lg:col-span-2" style={{ padding: "1.5rem" }} data-testid="settings-profile-card">
          <div className="text-sm font-medium mb-4" style={{ color: "#131822" }}>College profile</div>
          <label className="k-label mb-1 block">Display name</label>
          <input className="k-input mb-4" value={name} maxLength={200} onChange={(e) => setName(e.target.value)} data-testid="settings-name-input" />
          <label className="k-label mb-1 block">About your college</label>
          <textarea className="k-input klareet-serif" rows={6} value={about} maxLength={2000} onChange={(e) => setAbout(e.target.value)} data-testid="settings-about-input" />
          <div className="k-quiet !text-xs mt-1">This shows on your co-branded student entry page. Slug: <span className="klareet-mono">{settings?.slug}</span></div>
          <div className="flex items-center gap-3 mt-4">
            <button className="k-btn k-btn-primary" onClick={save} disabled={saving} data-testid="settings-save-btn">
              {saving ? <Loader2 size={15} className="animate-spin" /> : null} Save changes
            </button>
            {savedMsg && <span className="text-sm flex items-center gap-1.5" style={{ color: "#0E7A66" }} data-testid="settings-saved"><Check size={14} /> {savedMsg}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
